import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportExport } from "@/components/app/documents/report-export";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { PnlStatementTable } from "@/routes/_authenticated/_financials/pnl-report/-components/pnl-statement-table";
import { RevenueStatement } from "@/routes/_authenticated/_financials/pnl-report/-components/revenue-statement";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalInt, optionalOneOf, KEEP_PLACE } from "@/lib/search";

type PnlSearch = { mode?: "annual"; year?: number; month?: number; basis?: "cash" };

export const Route = createFileRoute("/_authenticated/_financials/pnl-report/")({
  validateSearch: (search: Record<string, unknown>): PnlSearch => ({
    mode: optionalOneOf(["annual"] as const, search.mode),
    year: optionalInt(search.year, 2000, 2100),
    month: optionalInt(search.month, 1, 12),
    basis: optionalOneOf(["cash"] as const, search.basis),
  }),
  component: ProfitAndLoss,
});

const now = new Date();

/** What came in against what went out, one month or a whole year. */
function ProfitAndLoss() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<PnlSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const mode: "month" | "annual" = params.mode ?? "month";
  const setMode = (next: "month" | "annual") =>
    patch({ mode: next === "annual" ? next : undefined });
  const year = params.year ?? now.getUTCFullYear();
  const month = params.month ?? now.getUTCMonth() + 1;
  const setYear = (next: number) =>
    patch({ year: next === now.getUTCFullYear() ? undefined : next });
  // One basis for the whole page, accrual first as the old P&L opened: the
  // statement's revenue, both net lines and the revenue-by-category year below.
  const basis: "accrual" | "cash" = params.basis ?? "accrual";
  const setBasis = (next: "accrual" | "cash") =>
    patch({ basis: next === "cash" ? next : undefined });

  // Absent month IS annual mode: the API reads it that way, and keeping the
  // toggle and the input in one object stops the two disagreeing.
  const input = { facilityId, year, month: mode === "month" ? month : undefined, basis };
  const report = useQuery(orpc.financials.reports.profitAndLossStatement.queryOptions({ input }));
  const download = useMutation(
    orpc.financials.reports.profitAndLossExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const period = `${year}-${String(month).padStart(2, "0")}`;

  return (
    <>
      <PageHeader
        title="P&L report"
        meta="Revenue against what the building spent, grouped the way an accountant reads it."
        actions={
          <ReportExport
            pending={download.isPending}
            onExport={(format) => download.mutate({ ...input, format })}
          />
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(next) => next && setMode(next as "month" | "annual")}
          variant="outline"
          aria-label="Period"
        >
          <ToggleGroupItem value="month">Month</ToggleGroupItem>
          <ToggleGroupItem value="annual">Year</ToggleGroupItem>
        </ToggleGroup>

        <ToggleGroup
          type="single"
          value={basis}
          onValueChange={(next) => next && setBasis(next as "accrual" | "cash")}
          variant="outline"
          aria-label="Revenue basis"
        >
          <ToggleGroupItem value="accrual">Accrual</ToggleGroupItem>
          <ToggleGroupItem value="cash">Cash</ToggleGroupItem>
        </ToggleGroup>

        {mode === "month" ? (
          <div className="space-y-1">
            <Label htmlFor="pnl-period">Month</Label>
            <Input
              id="pnl-period"
              type="month"
              className="w-40 pointer-coarse:w-48"
              value={period}
              onChange={(event) => {
                const [nextYear, nextMonth] = event.target.value.split("-");
                if (nextYear && nextMonth) {
                  patch({
                    year: Number(nextYear) === now.getUTCFullYear() ? undefined : Number(nextYear),
                    month:
                      Number(nextMonth) === now.getUTCMonth() + 1 ? undefined : Number(nextMonth),
                  });
                }
              }}
            />
          </div>
        ) : (
          <div className="space-y-1">
            <Label htmlFor="pnl-year">Year</Label>
            <Input
              id="pnl-year"
              type="number"
              className="w-28"
              min={2000}
              max={2100}
              value={year}
              onChange={(event) => setYear(Number(event.target.value) || now.getUTCFullYear())}
            />
          </div>
        )}
      </div>

      {report.data ? (
        <StatTiles>
          <StatTile label="Revenue billed" value={report.data.revenue.billed} />
          <StatTile label="Revenue received" value={report.data.revenue.received} />
          <StatTile label="Spent" value={report.data.summary.grandTotal} />
          <StatTile label="Net operating" value={report.data.summary.netOperating} />
          <StatTile label="Net" value={report.data.summary.net} />
        </StatTiles>
      ) : null}

      {report.isError ? (
        <ReadFailed
          message="The statement could not be loaded."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : report.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : report.data ? (
        <PnlStatementTable statement={report.data} />
      ) : null}

      <RevenueStatement facilityId={facilityId} year={year} basis={basis} />
    </>
  );
}
